/**
 * One scope of an ACL draft (S8 Task 8.11): Entry · Read · Write · Execute · **Effective** (the mask applied — named
 * users, named groups and the owning group are limited by it, finding S8-08), remove for named entries, and "Add user"
 * / "Add group" by object ID. Editing an entry never touches the mask (E5: `editEntryPermissions`); the mask row lives
 * in `AclMaskControls`. `onAdd` / `onRemove` default to the full-ACL helpers; the Propagate merge patch passes its own.
 */
import { useState } from 'react';
import { Button } from '../../../../../components/ui/button';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input } from '../../../../../components/ui/input';
import { formatPermissions } from '../../../../../core/engines/azureStorageAcl';
import { addNamedEntryDraft, editEntryPermissions, effectiveEntries, removeNamedEntryDraft, type AclEntryKey } from '../../../../../core/engines/azureStorageAclEffective';
import type { StorageAclEntry, StorageAclPermissions, StorageAclScope } from '../../../../../types/azureStorage';
import { namedEntryProblem } from '../aclDraft';

type Props = {
  entries: StorageAclEntry[];
  /** Shown as the only row while the scope has no entries (U28: a Merge patch starts empty). */
  emptyHint?: string;
  scope: StorageAclScope;
  displayNames?: Record<string, string>;
  /** A reason = every control disabled with it as `title` (read-only connection). */
  lockedReason?: string;
  onChange: (next: StorageAclEntry[]) => void;
  onAdd?: (entries: StorageAclEntry[], entry: StorageAclEntry) => StorageAclEntry[];
  onRemove?: (entries: StorageAclEntry[], key: AclEntryKey) => StorageAclEntry[];
  /** Without the Effective column (a merge patch has no complete ACL to evaluate). */
  hideEffective?: boolean;
};

const BITS: ReadonlyArray<{ bit: keyof StorageAclPermissions; label: string; letter: 'r' | 'w' | 'x' }> = [{ bit: 'read', label: 'Read', letter: 'r' }, { bit: 'write', label: 'Write', letter: 'w' }, { bit: 'execute', label: 'Execute', letter: 'x' }];
const same = (a: StorageAclPermissions, b: StorageAclPermissions): boolean => a.read === b.read && a.write === b.write && a.execute === b.execute;
const aclEntryTestId = (e: Pick<StorageAclEntry, 'scope' | 'type' | 'id'>): string => `hns-acl-entry-${e.scope}-${e.type}-${e.id ?? 'base'}`;

function who(entry: StorageAclEntry, names: Record<string, string>): string {
  if (!entry.id) return entry.type === 'user' ? 'Owning user' : entry.type === 'group' ? 'Owning group' : 'Other';
  const label = names[entry.id];
  return `${entry.type === 'user' ? 'User' : 'Group'} ${label ? `${label} (${entry.id})` : entry.id}`;
}

export function AclEntriesTable({ entries, scope, emptyHint, displayNames = {}, lockedReason, onChange, onAdd = addNamedEntryDraft, onRemove = removeNamedEntryDraft, hideEffective }: Props) {
  const [newId, setNewId] = useState('');
  const [problem, setProblem] = useState<string>();
  const rows = effectiveEntries(entries).filter(({ entry }) => entry.scope === scope && entry.type !== 'mask');
  const add = (type: 'user' | 'group') => {
    const reason = namedEntryProblem(entries, scope, type, newId);
    setProblem(reason);
    if (reason) return;
    onChange(onAdd(entries, { scope, type, id: newId.trim(), permissions: { read: true, write: false, execute: scope === 'default' } }));
    setNewId('');
  };
  return (
    <div className="grid gap-2" data-testid={`hns-acl-entries-${scope}`}>
      <table className="w-full text-xs">
        <thead><tr className="text-left text-muted-foreground"><th className="py-1 font-medium">Entry</th>{BITS.map(({ label }) => <th key={label} className="w-14 font-medium">{label}</th>)}{!hideEffective && <th className="font-medium">Effective</th>}<th /></tr></thead>
        <tbody>
          {rows.length === 0 && emptyHint && <tr className="border-t border-border"><td colSpan={hideEffective ? 5 : 6} className="py-2 text-muted-foreground" data-testid={`hns-acl-empty-${scope}`}>{emptyHint}</td></tr>}
          {rows.map(({ entry, effective }) => {
            const id = aclEntryTestId(entry);
            const limited = !same(effective, entry.permissions);
            return (
              <tr key={id} data-testid={id} className="border-t border-border">
                <td className="py-1 pr-2 break-words [overflow-wrap:anywhere]">{who(entry, displayNames)}</td>
                {BITS.map(({ bit, label, letter }) => (
                  <td key={bit}><Checkbox checked={entry.permissions[bit]} disabled={!!lockedReason} title={lockedReason} aria-label={`${label} for ${who(entry, displayNames)}`} data-testid={`${id}-${letter}`}
                    onCheckedChange={(checked) => onChange(editEntryPermissions(entries, entry, { ...entry.permissions, [bit]: checked === true }))} /></td>
                ))}
                {!hideEffective && <td data-testid={`${id}-effective`}><span className="font-mono">{formatPermissions(effective)}</span>{limited && <span className="ml-1 text-warning">Limited by the mask</span>}</td>}
                <td>{entry.id && <Button type="button" variant="ghost" size="xs" disabled={!!lockedReason} title={lockedReason ?? 'Remove this entry'} onClick={() => onChange(onRemove(entries, entry))} data-testid={`${id}-remove`}>Remove</Button>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="flex flex-wrap items-center gap-2">
        <Input className="h-7 w-72 text-xs" spellCheck={false} autoComplete="off" placeholder="Object ID of a user or group" value={newId} disabled={!!lockedReason} title={lockedReason} onChange={(event) => { setNewId(event.target.value); setProblem(undefined); }} aria-label={`New ${scope} entry object ID`} data-testid={`hns-acl-new-id-${scope}`} />
        <Button type="button" variant="outline" size="xs" disabled={!!lockedReason} title={lockedReason} onClick={() => add('user')} data-testid={`hns-acl-add-user-${scope}`}>Add user</Button>
        <Button type="button" variant="outline" size="xs" disabled={!!lockedReason} title={lockedReason} onClick={() => add('group')} data-testid={`hns-acl-add-group-${scope}`}>Add group</Button>
      </div>
      {problem && <p className="text-xs text-destructive" role="alert">{problem}</p>}
    </div>
  );
}
